<script setup>
import { h, ref, computed, onMounted } from 'vue';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useWindowSize, useEventListener } from '@vueuse/core';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarGroupHeader from './SidebarGroupHeader.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import { useRoute } from 'vue-router';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const {
  accountScopedRoute,
  currentAccount,
  isOnChatwootCloud,
  isCloudFeatureEnabled,
} = useAccount();
const store = useStore();
const route = useRoute();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');
const accountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const onboardingHub = computed(
  () => currentAccount.value?.onboarding_hub || null
);
const showOnboardingMenu = computed(() => {
  const accountMembership = (currentUser.value?.accounts || []).find(
    account => account.id === Number(accountId.value)
  );
  // Built-in administrator only — custom roles (e.g. Manager) stay out.
  if (accountMembership?.role !== 'administrator') return false;
  if (accountMembership?.custom_role_id) return false;
  if (
    !isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.ONBOARDING
    )
  ) {
    return false;
  }
  return Boolean(onboardingHub.value?.visible);
});
const onboardingProgressLabel = computed(() => {
  const completed = onboardingHub.value?.completed ?? 0;
  const total = onboardingHub.value?.total ?? 4;
  return `${completed}/${total}`;
});
const homeRoute = computed(() =>
  showOnboardingMenu.value
    ? accountScopedRoute('onboarding_home')
    : accountScopedRoute('copilot_home')
);

const isNotificationCenterActive = computed(() =>
  ['inbox_view', 'inbox_view_conversation'].includes(route.name)
);

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const isRTL = useMapGetter('accounts/isRTL');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const hasAdvancedAssignment = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ADVANCED_ASSIGNMENT
  );
});

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const planningSavedViews = useMapGetter('planningSavedViews/getRecords');

const PLANNING_SAVED_VIEW_ROUTES = {
  list: 'planning_events_view',
  board: 'planning_events_board_view',
  calendar: 'planning_calendar_view',
};

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
  if (isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING)) {
    store.dispatch('planningSavedViews/get');
  }
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const onComposeOpen = toggleFn => {
  toggleFn();
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
};

const onComposeClose = () => {
  emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

const menuItems = computed(() => {
  const items = [
    showOnboardingMenu.value && {
      name: 'Onboarding',
      label: t('SIDEBAR.ONBOARDING'),
      icon: 'i-lucide-rocket',
      activeOn: ['onboarding_home'],
      to: accountScopedRoute('onboarding_home'),
      countLabel: onboardingProgressLabel.value,
    },
    {
      name: 'Roo',
      label: t('COPILOT.SIDEBAR_TITLE'),
      icon: 'i-lucide-feather',
      activeOn: ['copilot_home'],
      to: accountScopedRoute('copilot_home'),
    },
    ...(isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING)
      ? [
          {
            name: 'Inbox',
            label: t('SIDEBAR.INBOX'),
            icon: 'i-lucide-inbox',
            to: accountScopedRoute('event_inbox'),
            activeOn: [
              'event_inbox',
              'event_inbox_event',
              'event_inbox_conversation',
            ],
          },
        ]
      : []),
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          children: teams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            icon: h(ChannelIcon, { inbox, class: 'size-[12px]' }),
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    // {
    //   name: 'Captain',
    //   icon: 'i-woot-captain',
    //   label: t('SIDEBAR.CAPTAIN'),
    //   activeOn: ['captain_assistants_create_index'],
    //   children: [
    //     {
    //       name: 'FAQs',
    //       label: t('SIDEBAR.CAPTAIN_RESPONSES'),
    //       activeOn: [
    //         'captain_assistants_responses_index',
    //         'captain_assistants_responses_pending',
    //       ],
    //       to: accountScopedRoute('captain_assistants_index', {
    //         navigationPath: 'captain_assistants_responses_index',
    //       }),
    //     },
    //     {
    //       name: 'Documents',
    //       label: t('SIDEBAR.CAPTAIN_DOCUMENTS'),
    //       activeOn: ['captain_assistants_documents_index'],
    //       to: accountScopedRoute('captain_assistants_index', {
    //         navigationPath: 'captain_assistants_documents_index',
    //       }),
    //     },
    //     {
    //       name: 'Scenarios',
    //       label: t('SIDEBAR.CAPTAIN_SCENARIOS'),
    //       activeOn: ['captain_assistants_scenarios_index'],
    //       to: accountScopedRoute('captain_assistants_index', {
    //         navigationPath: 'captain_assistants_scenarios_index',
    //       }),
    //     },
    //     {
    //       name: 'Playground',
    //       label: t('SIDEBAR.CAPTAIN_PLAYGROUND'),
    //       activeOn: ['captain_assistants_playground_index'],
    //       to: accountScopedRoute('captain_assistants_index', {
    //         navigationPath: 'captain_assistants_playground_index',
    //       }),
    //     },
    //     {
    //       name: 'Inboxes',
    //       label: t('SIDEBAR.CAPTAIN_INBOXES'),
    //       activeOn: ['captain_assistants_inboxes_index'],
    //       to: accountScopedRoute('captain_assistants_index', {
    //         navigationPath: 'captain_assistants_inboxes_index',
    //       }),
    //     },
    //     {
    //       name: 'Tools',
    //       label: t('SIDEBAR.CAPTAIN_TOOLS'),
    //       activeOn: ['captain_tools_index'],
    //       to: accountScopedRoute('captain_assistants_index', {
    //         navigationPath: 'captain_tools_index',
    //       }),
    //     },
    //     {
    //       name: 'Settings',
    //       label: t('SIDEBAR.CAPTAIN_SETTINGS'),
    //       activeOn: [
    //         'captain_assistants_settings_index',
    //         'captain_assistants_guidelines_index',
    //         'captain_assistants_guardrails_index',
    //       ],
    //       to: accountScopedRoute('captain_assistants_index', {
    //         navigationPath: 'captain_assistants_settings_index',
    //       }),
    //     },
    //   ],
    // },

    // Only show Events if feature flag is enabled
    isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING) && {
      name: 'Events',
      label: t('PLANNING.EVENTS.TITLE'),
      icon: 'i-lucide-calendar-days',
      activeOn: [
        'planning_events',
        'planning_events_view',
        'planning_events_stage',
        'planning_events_board',
        'planning_events_board_view',
        'planning_events_new',
        'planning_events_show',
        'planning_events_edit',
        'planning_calendar',
        'planning_calendar_view',
        'planning_find_slots',
      ],
      to: accountScopedRoute('planning_events'),
      disablePrefixActive: true,
      children: [
        {
          name: 'Events-all',
          label: 'All Events',
          to: accountScopedRoute('planning_events'),
          activeOn: ['planning_events'],
        },
        {
          name: 'Calendar',
          label: 'Calendar',
          to: accountScopedRoute('planning_calendar'),
          activeOn: ['planning_calendar'],
        },
        {
          name: 'Find-Slots',
          label: t('PLANNING.FIND_SLOTS.TITLE'),
          to: accountScopedRoute('planning_find_slots'),
          activeOn: ['planning_find_slots'],
          component: leafProps =>
            h('div', { class: 'flex flex-1 items-center gap-1.5 min-w-0' }, [
              h('span', { class: 'flex-1 truncate min-w-0' }, leafProps.label),
              h(
                'span',
                {
                  class:
                    'text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-10 flex-shrink-0 leading-none',
                },
                'Admin'
              ),
            ]),
        },
        ...(planningSavedViews.value.length
          ? [
              {
                name: 'Planning-Views',
                label: t('PLANNING.SAVED_VIEWS.SIDEBAR'),
                icon: 'i-lucide-bookmark',
                activeOn: [
                  'planning_events_view',
                  'planning_events_board_view',
                  'planning_calendar_view',
                ],
                children: planningSavedViews.value.map(view => ({
                  name: `Planning-View-${view.id}`,
                  label: view.name,
                  to: accountScopedRoute(
                    PLANNING_SAVED_VIEW_ROUTES[view.page_type] ||
                      PLANNING_SAVED_VIEW_ROUTES.list,
                    { viewId: view.id }
                  ),
                  activeOn: [
                    PLANNING_SAVED_VIEW_ROUTES[view.page_type] ||
                      PLANNING_SAVED_VIEW_ROUTES.list,
                  ],
                })),
              },
            ]
          : []),
      ],
    },
    isCloudFeatureEnabled(FEATURE_FLAGS.LEAD_NETWORKS) && {
      name: 'LeadNetworkPings',
      label: t('LEAD_NETWORK.PINGS.SIDEBAR'),
      icon: 'i-lucide-radio',
      activeOn: ['lead_network_pings', 'lead_network_ping_show'],
      to: accountScopedRoute('lead_network_pings'),
    },
    isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING) && {
      name: 'Lost & Found',
      label: t('PLANNING_LOST_AND_FOUND.TITLE'),
      icon: 'i-lucide-package-search',
      activeOn: [
        'planning_lost_and_found_objects',
        'planning_lost_and_found_objects_show',
        'planning_lost_and_found_reports',
        'planning_lost_and_found_reports_show',
        'planning_lost_objects',
        'planning_lost_objects_show',
      ],
      to: accountScopedRoute('planning_lost_and_found_objects'),
      disablePrefixActive: true,
      children: [
        {
          name: 'Lost-Objects',
          label: t('PLANNING_LOST_AND_FOUND.TABS.OBJECTS'),
          to: accountScopedRoute('planning_lost_and_found_objects'),
          activeOn: [
            'planning_lost_and_found_objects',
            'planning_lost_and_found_objects_show',
          ],
        },
        {
          name: 'Lost-Reports',
          label: t('PLANNING_LOST_AND_FOUND.TABS.REPORTS'),
          to: accountScopedRoute('planning_lost_and_found_reports'),
          activeOn: [
            'planning_lost_and_found_reports',
            'planning_lost_and_found_reports_show',
          ],
        },
      ],
    },
    isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING) && {
      name: 'Pricing',
      label: t('SIDEBAR.PRICING_PLANS'),
      icon: 'i-lucide-receipt',
      activeOn: [
        'settings_pricing_items',
        'settings_pricing_plans',
        'settings_pricing_plan_show',
        'settings_pricing_rule_edit',
        'settings_pricing_discount_codes',
      ],
      to: accountScopedRoute('settings_pricing_plans'),
      children: [
        {
          name: 'Rates',
          label: t('PRICING.MASTER_SHEET.TITLE'),
          to: accountScopedRoute('settings_pricing_items'),
          activeOn: ['settings_pricing_items'],
        },
        isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING_QUOTES) && {
          name: 'Discount codes',
          label: t('PRICING.DISCOUNT_CODES.TITLE'),
          to: accountScopedRoute('settings_pricing_discount_codes'),
          activeOn: ['settings_pricing_discount_codes'],
        },
        {
          name: 'Pricing Rules',
          label: t('PRICING.PLANS.TITLE'),
          to: accountScopedRoute('settings_pricing_plans'),
          activeOn: [
            'settings_pricing_plans',
            'settings_pricing_plan_show',
            'settings_pricing_rule_edit',
          ],
        },
      ].filter(Boolean),
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    {
      name: 'Companies',
      label: t('SIDEBAR.COMPANIES'),
      icon: 'i-lucide-building-2',
      children: [
        {
          name: 'All Companies',
          label: t('SIDEBAR.ALL_COMPANIES'),
          to: accountScopedRoute(
            'companies_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['companies_dashboard_index'],
        },
      ],
    },
    isCloudFeatureEnabled(FEATURE_FLAGS.SUPPLIERS) && {
      name: 'Suppliers',
      label: t('SIDEBAR.SUPPLIERS'),
      icon: 'i-lucide-truck',
      children: [
        {
          name: 'All Suppliers',
          label: t('SIDEBAR.ALL_SUPPLIERS'),
          to: accountScopedRoute(
            'suppliers_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['suppliers_dashboard_index'],
        },
      ],
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes.value,
        {
          name: 'Reports CSAT',
          label: t('SIDEBAR.CSAT'),
          to: accountScopedRoute('csat_reports'),
        },
        {
          name: 'Reports Bot',
          label: t('SIDEBAR.REPORTS_BOT'),
          to: accountScopedRoute('bot_reports'),
        },
        {
          name: 'Reports Events',
          label: t('SIDEBAR.REPORTS_EVENTS'),
          to: accountScopedRoute('events_reports'),
        },
        isCloudFeatureEnabled(FEATURE_FLAGS.BUZZ) && {
          name: 'Reports Buzz',
          label: t('SIDEBAR.REPORTS_BUZZ'),
          to: accountScopedRoute('buzz_reports'),
        },
        {
          name: 'Reports Widget',
          label: t('SIDEBAR.REPORTS_WIDGET'),
          to: accountScopedRoute('widget_load_reports'),
        },
      ].filter(Boolean),
    },
    {
      name: 'Campaigns',
      label: t('SIDEBAR.CAMPAIGNS'),
      icon: 'i-lucide-megaphone',
      children: [
        {
          name: 'Live chat',
          label: t('SIDEBAR.LIVE_CHAT'),
          to: accountScopedRoute('campaigns_livechat_index'),
        },
        {
          name: 'SMS',
          label: t('SIDEBAR.SMS'),
          to: accountScopedRoute('campaigns_sms_index'),
        },
        {
          name: 'WhatsApp',
          label: t('SIDEBAR.WHATSAPP'),
          to: accountScopedRoute('campaigns_whatsapp_index'),
        },
      ],
    },
    {
      name: 'Portals',
      label: t('SIDEBAR.HELP_CENTER.TITLE'),
      icon: 'i-lucide-library-big',
      children: [
        {
          name: 'Articles',
          label: t('SIDEBAR.HELP_CENTER.ARTICLES'),
          activeOn: [
            'portals_articles_index',
            'portals_articles_new',
            'portals_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_articles_index',
          }),
        },
        {
          name: 'Categories',
          label: t('SIDEBAR.HELP_CENTER.CATEGORIES'),
          activeOn: [
            'portals_categories_index',
            'portals_categories_articles_index',
            'portals_categories_articles_edit',
          ],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_categories_index',
          }),
        },
        {
          name: 'Locales',
          label: t('SIDEBAR.HELP_CENTER.LOCALES'),
          activeOn: ['portals_locales_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_locales_index',
          }),
        },
        {
          name: 'Settings',
          label: t('SIDEBAR.HELP_CENTER.SETTINGS'),
          activeOn: ['portals_settings_index'],
          to: accountScopedRoute('portals_index', {
            navigationPath: 'portals_settings_index',
          }),
        },
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-settings-2',
      children: [
        {
          name: 'Settings Assistant',
          label: t('SIDEBAR.ASSISTANTS'),
          icon: 'i-lucide-bot',
          to: accountScopedRoute('settings_assistant_list'),
          activeOn: ['settings_assistant_list'],
        },
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          to: accountScopedRoute('settings_teams_list'),
        },
        ...(hasAdvancedAssignment.value
          ? [
              {
                name: 'Settings Agent Assignment',
                label: t('SIDEBAR.AGENT_ASSIGNMENT'),
                icon: 'i-lucide-user-cog',
                to: accountScopedRoute('assignment_policy_index'),
              },
            ]
          : []),
        {
          name: 'Settings Inboxes',
          label: t('SIDEBAR.INBOXES'),
          icon: 'i-lucide-inbox',
          to: accountScopedRoute('settings_inbox_list'),
        },
        {
          name: 'Settings Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tags',
          to: accountScopedRoute('labels_list'),
        },
        {
          name: 'Settings Custom Attributes',
          label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
          icon: 'i-lucide-code',
          to: accountScopedRoute('attributes_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-repeat',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Settings Macros',
          label: t('SIDEBAR.MACROS'),
          icon: 'i-lucide-toy-brick',
          to: accountScopedRoute('macros_wrapper'),
        },
        {
          name: 'Settings Canned Responses',
          label: t('SIDEBAR.CANNED_RESPONSES'),
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
        },
        {
          name: 'Knowledgebase',
          label: t('SIDEBAR.KNOWLEDGEBASE'),
          icon: 'i-lucide-library',
          to: accountScopedRoute('settings_knowledgebases_index'),
        },
        isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING) && {
          name: 'Settings Spaces',
          label: t('SETTINGS.SPACES.TITLE'),
          icon: 'i-lucide-orbit',
          to: accountScopedRoute('settings_spaces_index'),
        },
        isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING) &&
          isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING_RESOURCES) && {
            name: 'Settings Resources',
            label: t('SETTINGS.RESOURCES.TITLE'),
            icon: 'i-lucide-boxes',
            to: accountScopedRoute('settings_resources_index'),
          },
        isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING) && {
          name: 'Planning Experiences',
          label: t('PLANNING_EXPERIENCES.HEADER'),
          icon: 'i-lucide-sparkles',
          to: accountScopedRoute('planning_experiences_index'),
        },
        isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING) && {
          name: 'Booking Widgets',
          label: t('SIDEBAR.BOOKING_WIDGETS'),
          icon: 'i-lucide-calendar-check-2',
          to: accountScopedRoute('settings_booking_widgets_index'),
          activeOn: [
            'settings_booking_widgets_index',
            'settings_booking_widgets_new',
            'settings_booking_widgets_show',
          ],
        },
        isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING) && {
          name: 'Planning Event Statuses',
          label: t('SIDEBAR.EVENT_STATUSES'),
          icon: 'i-lucide-gauge',
          to: accountScopedRoute('settings_planning_event_statuses'),
        },
        {
          name: 'Settings Integrations',
          label: t('SIDEBAR.INTEGRATIONS'),
          icon: 'i-lucide-blocks',
          to: accountScopedRoute('settings_applications'),
        },
        {
          name: 'Settings Data Hub',
          label: t('SIDEBAR.DATA_HUB'),
          icon: 'i-lucide-refresh-cw',
          to: accountScopedRoute('settings_data_hub_index'),
          activeOn: [
            'settings_data_hub_index',
            'settings_data_hub_new',
            'settings_data_hub_edit',
          ],
        },
        // {
        //   name: 'Settings Audit Logs',
        //   label: t('SIDEBAR.AUDIT_LOGS'),
        //   icon: 'i-lucide-briefcase',
        //   to: accountScopedRoute('auditlogs_list'),
        // },
        {
          name: 'Settings Custom Roles',
          label: t('SIDEBAR.CUSTOM_ROLES'),
          icon: 'i-lucide-shield-plus',
          to: accountScopedRoute('custom_roles_list'),
        },
        // {
        //   name: 'Settings Sla',
        //   label: t('SIDEBAR.SLA'),
        //   icon: 'i-lucide-clock-alert',
        //   to: accountScopedRoute('sla_list'),
        // },
        {
          name: 'Workflows',
          label: t('SIDEBAR.WORKFLOWS'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('workflows_index'),
        },
        // {
        //   name: 'Settings Security',
        //   label: t('SIDEBAR.SECURITY'),
        //   icon: 'i-lucide-shield',
        //   to: accountScopedRoute('security_settings_index'),
        // },
        {
          name: 'Settings Billing',
          label: t('SIDEBAR.BILLING'),
          icon: 'i-lucide-credit-card',
          to: accountScopedRoute('billing_settings_index'),
        },
        isCloudFeatureEnabled(FEATURE_FLAGS.LEAD_NETWORKS) && {
          name: 'Settings Lead Networks',
          label: t('LEAD_NETWORK.SETTINGS.TITLE'),
          icon: 'i-lucide-network',
          to: accountScopedRoute('settings_lead_networks'),
        },
        {
          name: 'Settings Email Templates',
          label: t('SIDEBAR.EMAIL_TEMPLATES'),
          icon: 'i-lucide-mail',
          to: accountScopedRoute('email_templates_index'),
        },
        {
          name: 'Settings Audit Logs',
          label: t('SIDEBAR.AUDIT_LOGS'),
          icon: 'i-lucide-key-round',
          to: accountScopedRoute('auditlogs_list'),
        },
        {
          name: 'Settings Account Settings',
          label: t('SIDEBAR.ACCOUNT_SETTINGS'),
          icon: 'i-lucide-briefcase',
          to: accountScopedRoute('general_settings_index'),
        },
      ].filter(Boolean),
    },
  ];
  // Filter out any falsey items (e.g. Planning if not enabled)
  return items.filter(Boolean);
});
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="dark w-[16rem] bg-n-solid-2 rtl:border-l ltr:border-r border-n-weak flex flex-col text-sm pb-1 fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 md:static md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-in-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <section class="grid gap-2 my-6">
      <div
        class="flex items-center min-w-0 gap-2"
        :class="{
          'justify-center px-1': isEffectivelyCollapsed,
          'px-4 pb-3': !isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <SidebarAccountSwitcher
            is-collapsed
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
        <template v-else>
          <RouterLink
            :to="homeRoute"
            class="grid flex-shrink-0 size-7 place-content-center"
          >
            <Logo class="size-7" />
          </RouterLink>
          <SidebarAccountSwitcher
            class="flex-grow min-w-0"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
      </div>
      <div
        class="flex gap-2"
        :class="isEffectivelyCollapsed ? 'flex-col items-center px-1' : 'px-4'"
      >
        <RouterLink
          v-if="!isEffectivelyCollapsed"
          :to="{ name: 'search' }"
          class="flex items-center w-full gap-2 px-4 py-2 rounded-lg outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30"
        >
          <span class="flex-shrink-0 i-lucide-search size-5 text-n-slate-11" />
          <span class="flex-grow text-left">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <RouterLink
          v-else
          :to="{ name: 'search' }"
          class="flex items-center justify-center size-10 rounded-lg outline outline-1 outline-n-weak bg-n-solid-3 dark:bg-n-black/30"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-lucide-search size-5 text-n-slate-11" />
        </RouterLink>
        <ComposeConversation align-position="right" @close="onComposeClose">
          <template #trigger="{ toggle }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              :size="isEffectivelyCollapsed ? 'md' : 'md'"
              class="!bg-n-solid-3 dark:!bg-n-black/30 !outline-n-weak !text-n-slate-11"
              :class="{ '!size-10': isEffectivelyCollapsed }"
              @click="onComposeOpen(toggle)"
            />
          </template>
        </ComposeConversation>
      </div>
    </section>
    <nav
      class="flex-grow gap-2 pb-5 overflow-y-scroll no-scrollbar"
      :class="isEffectivelyCollapsed ? 'px-1' : 'px-4'"
    >
      <ul
        class="flex flex-col gap-2 m-0 list-none"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <div
        class="pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-solid-2 to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          !isEffectivelyCollapsed
        "
      />
      <SidebarChangelogButton
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          isEffectivelyCollapsed
        "
      />
      <div
        class="flex-shrink-0 w-full border-t border-n-weak shadow-[0px_-2px_4px_0px_rgba(27,28,29,0.02)]"
        :class="
          isEffectivelyCollapsed ? 'flex justify-center px-1 py-2' : 'px-3 pt-2'
        "
      >
        <SidebarGroupHeader
          :label="t('SIDEBAR.NOTIFICATION_CENTER')"
          icon="i-lucide-bell"
          :to="accountScopedRoute('inbox_view')"
          :is-active="isNotificationCenterActive"
          :is-collapsed="isEffectivelyCollapsed"
          :getter-keys="{ count: 'notifications/getUnreadCount' }"
        />
      </div>
      <div
        class="p-3 flex-shrink-0 flex w-full gap-2 items-center"
        :class="isEffectivelyCollapsed ? 'justify-center' : 'justify-between'"
      >
        <SidebarProfileMenu
          :is-collapsed="isEffectivelyCollapsed"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </section>
    <!-- Resize Handle (desktop only) -->
    <div
      class="hidden md:block absolute top-0 h-full w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
  </aside>
</template>
